import { ref, computed } from 'vue'
import { listAgents } from '@/services/wootdashAgents'
import {
  listInternalMessages, sendInternalMessage, getUnreadInternalMessageCounts, type CpInternalMessage,
} from '@/services/wootdashInternalChat'
import { onInternalMessage } from '@/composables/useConversationRealtime'
import { showPushNotification } from '@/services/notifications'

// Chat entre agentes nativo do WootDash (Guid real, GET/POST /mensagens-internas) —
// fork de useAgentChat.ts (Chatwoot/n8n, id numérico), que continua servindo
// BroadcastComposeModal.vue/useDashboardEngine.ts sem mudança (fora de escopo). Mesma forma
// pública (agents/unreadByAgent/activeAgentId/...), só troca polling por SignalR
// (onInternalMessage, evento "MensagemInterna") + um poll leve de segurança pras não-lidas.

export interface ChatAgentCp {
  id: string
  name: string
  email: string
  avatarUrl: string
  availability: 'online' | 'busy' | 'offline'
  /** Perfil (o primeiro do RBAC) — cartão de detalhes da janela Conversas. */
  role?: string
}

const STATUS_ORDER: Record<string, number> = { online: 0, busy: 1, offline: 2 }

// Estado de módulo (singleton) — mesmo padrão de useAgentChat/useFloatChats/useAdmin.
const agents          = ref<ChatAgentCp[]>([])
const unreadByAgent   = ref<Record<string, number>>({})
const messagesByAgent = ref<Record<string, CpInternalMessage[]>>({})
const activeAgentId   = ref<string | null>(null)
const panelOpen        = ref(false)
const loadingAgents    = ref(false)
const loadingMessages  = ref(false)
const sendError        = ref('')

let unsubscribeRealtime: (() => void) | null = null
let pollTimer: ReturnType<typeof setInterval> | null = null
let currentToken = ''
let currentMyId  = ''

function isTabFocused(): boolean {
  return document.visibilityState === 'visible' && document.hasFocus()
}
function agentName(id: string): string {
  return agents.value.find(a => a.id === id)?.name ?? 'Um agente'
}

export function useAgentChatCp() {
  const totalUnread = computed(() =>
    Object.values(unreadByAgent.value).reduce((s, n) => s + n, 0)
  )
  const activeAgent = computed(() =>
    agents.value.find(a => a.id === activeAgentId.value) ?? null
  )
  const activeMessages = computed(() =>
    activeAgentId.value != null ? (messagesByAgent.value[activeAgentId.value] ?? []) : []
  )

  async function loadAgents(token: string, myId: string) {
    if (loadingAgents.value) return
    loadingAgents.value = true
    try {
      const { agents: list } = await listAgents(token)
      agents.value = list
        .filter(a => a.id !== myId)
        .map((a): ChatAgentCp => ({
          id: a.id,
          name: `${a.firstName} ${a.lastName}`.trim(),
          email: a.email,
          avatarUrl: a.avatarUrl ?? '',
          availability: a.status.toLowerCase() as ChatAgentCp['availability'],
          role: a.roleNames?.[0] ?? '',
        }))
        .sort((a, b) => {
          const byStatus = (STATUS_ORDER[a.availability] ?? 2) - (STATUS_ORDER[b.availability] ?? 2)
          return byStatus !== 0 ? byStatus : a.name.localeCompare(b.name, 'pt-BR')
        })
    } catch (err) {
      console.warn('[AgentChatCp] loadAgents:', err)
    } finally {
      loadingAgents.value = false
    }
  }

  async function loadUnread(token: string) {
    try {
      unreadByAgent.value = await getUnreadInternalMessageCounts(token)
    } catch (err) {
      console.warn('[AgentChatCp] loadUnread:', err)
    }
  }

  async function openThread(token: string, otherId: string) {
    activeAgentId.value = otherId
    loadingMessages.value = true
    try {
      // Listar já marca como lida server-side (ver ListInternalMessagesCommandHandler) —
      // sem chamada separada de "marcar como lida" como o sistema antigo tinha.
      messagesByAgent.value[otherId] = await listInternalMessages(token, otherId)
      unreadByAgent.value = { ...unreadByAgent.value, [otherId]: 0 }
    } catch (err) {
      console.warn('[AgentChatCp] openThread:', err)
    } finally {
      loadingMessages.value = false
    }
  }

  function backToList() { activeAgentId.value = null }

  function togglePanel() {
    panelOpen.value = !panelOpen.value
    if (!panelOpen.value) activeAgentId.value = null
  }

  function closePanel() {
    panelOpen.value = false
    activeAgentId.value = null
  }

  async function send(token: string, otherId: string, body: string) {
    const text = body.trim()
    if (!text) return
    sendError.value = ''
    try {
      const message = await sendInternalMessage(token, otherId, text)
      if (!message) throw new Error('Falha ao enviar')
      const current = messagesByAgent.value[otherId] ?? []
      messagesByAgent.value = { ...messagesByAgent.value, [otherId]: [...current, message] }
    } catch (err) {
      console.warn('[AgentChatCp] send:', err)
      sendError.value = 'Não foi possível enviar a mensagem.'
    }
  }

  /** Idempotente — chamar de novo com a mesma sessão já rodando só atualiza token/myId. */
  function startRealtime(token: string, myId: string) {
    currentToken = token
    currentMyId  = myId
    if (unsubscribeRealtime) return

    unsubscribeRealtime = onInternalMessage(evt => {
      if (evt.receiverId !== currentMyId) return
      const senderId = evt.senderId

      if (activeAgentId.value === senderId) {
        const current = messagesByAgent.value[senderId] ?? []
        messagesByAgent.value = {
          ...messagesByAgent.value,
          [senderId]: [...current, {
            id: evt.messageId, senderId, receiverId: currentMyId, body: evt.body, createdAt: evt.createdAt, readAt: null,
          }],
        }
        // Thread aberta na hora que a mensagem chega — o push não marca como lida sozinho
        // (só o GET faz isso), então refaz a listagem pra sincronizar o ReadAt real.
        listInternalMessages(currentToken, senderId).then(msgs => {
          messagesByAgent.value = { ...messagesByAgent.value, [senderId]: msgs }
        })
      } else {
        unreadByAgent.value = { ...unreadByAgent.value, [senderId]: (unreadByAgent.value[senderId] ?? 0) + 1 }
        if (!isTabFocused()) {
          showPushNotification(
            agentName(senderId), evt.body, window.location.href, `agent-chat-cp-${senderId}`,
            undefined, { data: { type: 'agent-chat-cp', senderId } },
          )
        }
      }
    })

    // Rede de segurança leve (60s) pra sincronizar não-lidas caso um evento se perca —
    // sem backoff/circuito, mesmo critério já usado no resto do app WootDash.
    if (!pollTimer) pollTimer = setInterval(() => loadUnread(currentToken), 60_000)
  }

  function stopRealtime() {
    unsubscribeRealtime?.()
    unsubscribeRealtime = null
    if (pollTimer) { clearInterval(pollTimer); pollTimer = null }
    currentToken = ''
    currentMyId  = ''
  }

  // Chamado no logout — mesmo motivo do useAgentChat.reset()/useFloatChats.closeAll().
  function reset() {
    stopRealtime()
    agents.value = []
    unreadByAgent.value = {}
    messagesByAgent.value = {}
    activeAgentId.value = null
    panelOpen.value = false
    sendError.value = ''
  }

  return {
    agents,
    unreadByAgent,
    totalUnread,
    activeAgentId,
    activeAgent,
    activeMessages,
    panelOpen,
    loadingAgents,
    loadingMessages,
    sendError,
    loadAgents,
    loadUnread,
    openThread,
    backToList,
    togglePanel,
    closePanel,
    send,
    startRealtime,
    stopRealtime,
    reset,
  }
}
